/**
 * The dealer portal's RidehailsHost — everything app-specific the shared
 * @hopdrive/feature-ridehails needs, assembled from this app's auth, config,
 * router, and customer config. Wrap any surface that renders the feature's
 * components (the board page, the details page, MoveDetailsRide's actions).
 */
import React from 'react';
import { RidehailsHostProvider, type RidehailsHost } from '@hopdrive/feature-ridehails';
import { authService } from '@features/auth/services/authService';
import { useCustomerConfig } from '@store';
import config from '@config';
import { movePath, ridesharePath } from '@features/products/productRoutes';

export const DealerRidehailsHost: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const customerConfig = useCustomerConfig();

  const host: RidehailsHost = React.useMemo(() => {
    const isDevelopment =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return {
      // The auth service can resolve undefined mid-refresh; the old inline code sent
      // whatever it got, so preserve that behavior rather than throwing here.
      getAccessToken: async () => (await authService.getAccessTokenWithAutoRefresh()) ?? '',
      ridesBaseUrl: config.hopridesBaseUrl,
      devProxyOrigin: isDevelopment ? window.location.origin : null,
      ridehailHref: moveId => ridesharePath(moveId),
      moveHref: moveId => movePath(moveId),
      referenceNumLabel: customerConfig?.reference_num_label ?? null,
      actionsEnabled: config.ridehail.enabled,
    };
  }, [customerConfig]);

  return <RidehailsHostProvider host={host}>{children}</RidehailsHostProvider>;
};
